import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Mail, MessageCircle, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { WA_GREEN } from "@/utils/whatsapp";

type Channel = "email" | "whatsapp";
type Kind = "student" | "parent";
type Preview = { results: number; students: number; parents: number; whatsapp_connected: boolean | null;
    channels: Record<Channel, { recipients: number; with_contact: number; sent: number; no_contact: number; failed: number }> };

const errKey = (e: any) => e?.response?.data?.detail?.error;

function NotifyDialog({ examId, channel, onClose }: { examId: string; channel: Channel; onClose: () => void }) {
    const { t } = useTranslation();
    const [kinds, setKinds] = useState<Kind[]>(["student", "parent"]);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    const loadPreview = async (k: Kind[]) => {
        setLoading(true);
        try { const r = await api.post(`/exams/${examId}/notify`, { channels: [channel], recipients: k, dry_run: true }); setPreview(r.data); }
        catch (e: any) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { loadPreview(["student", "parent"]); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (k: Kind) => {
        const next = kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k];
        if (!next.length) return;
        setKinds(next); loadPreview(next);
    };

    const send = async () => {
        setSending(true);
        try {
            const r = await api.post(`/exams/${examId}/notify`, { channels: [channel], recipients: kinds });
            const c = r.data.channels[channel];
            toast.success(t("exams.notify.sent", { sent: c.sent, skipped: c.no_contact + c.failed }));
            onClose();
        } catch (e: any) {
            const k = errKey(e);
            toast.error(k ? t(`exams.notify.err.${k}` as any, { defaultValue: formatApiError(e) }) : formatApiError(e));
        } finally { setSending(false); }
    };

    const c = preview?.channels[channel];
    const blocked = channel === "whatsapp" && preview?.whatsapp_connected === false;
    const color = channel === "whatsapp" ? WA_GREEN : "var(--primary)";
    return (
        <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }}>
                    <Dialog.Content data-testid="exam-notify-dialog" aria-describedby={undefined} className="w-full max-w-md rounded-2xl border p-5 space-y-4 outline-none" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                        <div className="flex items-start justify-between gap-3">
                            <Dialog.Title className="text-lg font-semibold" style={{ color: "var(--text)" }}>{t(channel === "email" ? "exams.notify.email_title" : "exams.notify.wa_title")}</Dialog.Title>
                            <Dialog.Close data-testid="exam-notify-close" aria-label={t("common.close")} className="p-1 rounded hover:bg-[var(--input)]" style={{ color: "var(--text-muted)" }}><X size={16} /></Dialog.Close>
                        </div>
                        <div className="flex gap-2">
                            {(["student", "parent"] as Kind[]).map((k) => (
                                <label key={k} data-testid={`exam-notify-kind-${k}`} className="flex-1 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer" style={{ borderColor: kinds.includes(k) ? color : "var(--border)", color: "var(--text)" }}>
                                    <input type="checkbox" checked={kinds.includes(k)} onChange={() => toggle(k)} />{t(`exams.notify.kind.${k}`)}
                                </label>
                            ))}
                        </div>
                        {loading || !c ? <div className="flex justify-center py-6"><Loader2 className="animate-spin" size={20} /></div> : (
                            <div className="rounded-xl border p-3 text-sm space-y-1" style={{ background: "rgba(108,54,231,0.07)", borderColor: "rgba(108,54,231,0.2)", color: "var(--text)" }} data-testid="exam-notify-preview">
                                <div>{t("exams.notify.summary", { students: preview!.students, parents: preview!.parents })}</div>
                                <div className="font-semibold" data-testid="exam-notify-reachable">{t("exams.notify.reachable", { n: c.with_contact, total: c.recipients })}</div>
                                {c.recipients > c.with_contact && <div className="text-xs" style={{ color: "var(--warning-fg)" }}>{t(channel === "email" ? "exams.notify.no_email" : "exams.notify.no_phone", { n: c.recipients - c.with_contact })}</div>}
                                {preview!.results === 0 && <div className="text-xs" style={{ color: "var(--error-fg)" }} data-testid="exam-notify-no-results">{t("exams.notify.err.no_results")}</div>}
                                {blocked && <div className="text-xs" style={{ color: "var(--error-fg)" }} data-testid="exam-notify-wa-off">{t("exams.notify.err.whatsapp_not_connected")}</div>}
                            </div>
                        )}
                        <button data-testid="exam-notify-confirm" onClick={send} disabled={sending || loading || !c?.with_contact || blocked || !preview?.results}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 text-sm font-medium text-white disabled:opacity-50" style={{ background: color, minHeight: 42 }}>
                            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{t("exams.notify.confirm")}
                        </button>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

export function ExamNotifyActions({ examId }: { examId: string }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState<Channel | null>(null);
    const btn = "inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-[var(--input)]";
    return (
        <div className="flex flex-wrap gap-2" data-testid="exam-notify-actions">
            <button data-testid="exam-notify-email" onClick={() => setOpen("email")} className={btn} style={{ borderColor: "var(--border)", color: "var(--text)" }}>
                <Mail size={15} style={{ color: "var(--primary)" }} />{t("exams.notify.email_btn")}
            </button>
            <button data-testid="exam-notify-whatsapp" onClick={() => setOpen("whatsapp")} className={btn} style={{ borderColor: WA_GREEN, color: "var(--text)" }}>
                <MessageCircle size={15} style={{ color: WA_GREEN }} />{t("exams.notify.wa_btn")}
            </button>
            {open && <NotifyDialog examId={examId} channel={open} onClose={() => setOpen(null)} />}
        </div>
    );
}
